/**
 * Raw control-size ladder data — the single numeric source of truth for the
 * control-size system. Plain ES module (.mjs) so both consumers import without
 * a transpile step:
 *   - `src/theme/control-size.ts` layers TypeScript types on top.
 *   - `plugins/vite-plugin-control-tokens.ts` imports at Node.js startup to
 *     derive CSS tokens without mirroring the values.
 *
 * Edit ONLY this file to change size values.
 *
 * Design-system provenance: five-step height ladder (xxs 24 → xs 32 → sm 40 →
 * md 48 → lg 56 px). Label font follows a two-step progression (10/12/14/14/16
 * px), with sm and md sharing 14 px at distinct heights. Padding widens per
 * role (button > input > chip) and per size. Gap uses the standard 2 px
 * increment per step. This file is the canonical source; downstream CSS tokens
 * and TypeScript types are derived from it.
 */

/** @type {readonly ['xxs', 'xs', 'sm', 'md', 'lg']} */
export const CONTROL_SIZES = /** @type {const} */ (['xxs', 'xs', 'sm', 'md', 'lg'])

/** @type {Record<'xxs'|'xs'|'sm'|'md'|'lg', { height: number; labelFont: number; labelLeading: number; pad: Record<'button'|'input'|'chip', number>; gap: number; icon: Record<'button'|'input'|'chip', number> }>} */
export const CONTROL_SIZE = {
	xxs: {
		height: 24,
		labelFont: 10,
		labelLeading: 12, // button.ts:346 `leading-3` = 12px
		pad: { button: 8, input: 8, chip: 8 },
		gap: 2, // button.ts:353 gap-0.5 = 2px
		icon: { button: 12, input: 12, chip: 12 },
	},
	xs: {
		height: 32,
		labelFont: 12,
		labelLeading: 16, // leading-4 = 16px
		pad: { button: 16, input: 12, chip: 10 },
		gap: 4, // button.ts:354 gap-1 = 4px
		icon: { button: 16, input: 16, chip: 16 },
	},
	sm: {
		height: 40,
		labelFont: 14,
		labelLeading: 20, // leading-5 = 20px
		pad: { button: 20, input: 16, chip: 12 },
		gap: 6, // button.ts:355 gap-1.5 = 6px
		icon: { button: 20, input: 20, chip: 20 },
	},
	md: {
		height: 48,
		labelFont: 14, // same as sm; distinct heights, one label scale (plan § md-vs-sm)
		labelLeading: 20, // leading-5 = 20px
		pad: { button: 24, input: 16, chip: 14 },
		gap: 8, // button.ts:356 gap-2 = 8px
		icon: { button: 24, input: 24, chip: 24 },
	},
	lg: {
		height: 56,
		labelFont: 16,
		labelLeading: 24, // leading-6 = 24px
		pad: { button: 28, input: 20, chip: 16 },
		gap: 10, // button.ts:357 gap-2.5 = 10px
		icon: { button: 24, input: 24, chip: 24 },
	},
}
